import type { Quantity, UnitCode } from '~/graphql/graphql';
import {
  hasDimensions,
  toStandardUnits,
  type Dimensions,
} from './unitConversion';

type OptionalNumber = number | null | undefined;
type OptionalQuantity = Quantity | null | undefined;

export type CartQuantity =
  | { kind: 'empty' }
  | { kind: 'measured'; quantity: Quantity }
  | { kind: 'unmeasurable' };

export type StockPipeline = {
  currentStock: OptionalQuantity;
  onOrderStock?: OptionalQuantity;
  inboundStock?: OptionalQuantity;
  cartQuantity?: OptionalQuantity;
};

export type TargetOrderSizeInput = {
  usageRatePerWeek: OptionalQuantity;
  targetWeeksCovered: OptionalNumber;
  currentStock: OptionalQuantity;
  onOrderStock?: OptionalQuantity;
  inboundStock?: OptionalQuantity;
  cartQuantity: CartQuantity;
  /** Unit of the result; defaults to the unit of the current stock. */
  targetUnit?: UnitCode;
  dims: Dimensions;
};

export function quantityInUnit(
  quantity: OptionalQuantity,
  unit: UnitCode,
  dims: Dimensions
): number | null {
  if (!quantity) return 0;
  if (quantity.unit === unit) return quantity.value;
  if (!hasDimensions(dims)) return null;
  const { lengthInches, widthInches, thicknessInches } = dims;
  return toStandardUnits(
    quantity.value,
    quantity.unit,
    unit,
    lengthInches,
    widthInches,
    thicknessInches
  );
}

/**
 * A seller's minimum order, set in the seller's own unit, restated in `unit`. Falls back to
 * one unit when none is set; null when a set minimum cannot be converted.
 */
export function minimumOrderInUnit(
  minimumOrder: OptionalQuantity,
  unit: UnitCode,
  dims: Dimensions
): number | null {
  if (!minimumOrder) return 1;
  const converted = quantityInUnit(minimumOrder, unit, dims);
  return converted === null ? null : converted || 1;
}

/**
 * The single definition of "material already accounted for": current stock plus
 * on-order plus inbound plus anything already in the cart, every part restated in
 * `stockUnit` so every caller reconciles units the same way. Weeks-of-cover passes no
 * cart quantity and target order size does, so the two figures differ by the cart.
 * Returns null when a part carries a unit the product dimensions cannot reconcile.
 *
 * @example
 * stockPipelineInUnit(
 *   { currentStock: { value: 50, unit: 'PCS' }, onOrderStock: { value: 1, unit: 'MBF' } },
 *   'PCS',
 *   { lengthInches: 96, widthInches: 3.5, thicknessInches: 1.5 }
 * );
 */
export function stockPipelineInUnit(
  { currentStock, onOrderStock, inboundStock, cartQuantity }: StockPipeline,
  stockUnit: UnitCode,
  dims: Dimensions
): number | null {
  let totalInStockUnit = 0;
  for (const heldQuantity of [
    currentStock,
    onOrderStock,
    inboundStock,
    cartQuantity,
  ]) {
    const convertedQuantity = quantityInUnit(heldQuantity, stockUnit, dims);
    if (convertedQuantity === null) return null;
    totalInStockUnit += convertedQuantity;
  }
  return totalInStockUnit;
}

/**
 * Quantity to order so the material already held, already on order, inbound and
 * in the cart covers the target weeks of usage, expressed in `targetUnit` (else the
 * unit of the current stock), rounded to 3dp (whole pieces for PCS). Returns 0 when nothing is needed, and null when an input uses a
 * different unit that the product dimensions cannot convert — including a cart
 * that holds something it cannot measure — so a caller can tell "order nothing"
 * apart from "no suggestion available".
 *
 * @example
 * calculateTargetOrderSize({
 *   usageRatePerWeek: { value: 0.35, unit: 'MBF' },
 *   targetWeeksCovered: 4,
 *   currentStock: { value: 50, unit: 'PCS' },
 *   onOrderStock: { value: 10, unit: 'PCS' },
 *   inboundStock: { value: 5, unit: 'PCS' },
 *   cartQuantity: { kind: 'empty' },
 *   dims: { lengthInches: 96, widthInches: 3.5, thicknessInches: 1.5 },
 * });
 */
export function calculateTargetOrderSize({
  usageRatePerWeek,
  targetWeeksCovered,
  currentStock,
  onOrderStock,
  inboundStock,
  cartQuantity,
  targetUnit,
  dims,
}: TargetOrderSizeInput): number | null {
  if (cartQuantity.kind === 'unmeasurable') return null;

  const unit = targetUnit ?? currentStock?.unit ?? usageRatePerWeek?.unit;
  if (!unit) return 0;

  const cartQuantityHeld =
    cartQuantity.kind === 'measured' ? cartQuantity.quantity : null;

  const convertedUsageRate = quantityInUnit(usageRatePerWeek, unit, dims);
  if (convertedUsageRate === null) return null;

  const quantityAlreadyCovered = stockPipelineInUnit(
    {
      currentStock,
      onOrderStock,
      inboundStock,
      cartQuantity: cartQuantityHeld,
    },
    unit,
    dims
  );
  if (quantityAlreadyCovered === null) return null;

  const targetOrderSize =
    convertedUsageRate * (targetWeeksCovered ?? 0) - quantityAlreadyCovered;
  if (targetOrderSize <= 0) return 0;
  return unit === 'PCS'
    ? Math.floor(targetOrderSize)
    : Math.round(targetOrderSize * 1000) / 1000;
}
